import { useNavigation } from '@react-navigation/native';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Text, View, ScrollView, SafeAreaView, Platform } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CommonActions } from '@react-navigation/native';
import { contactUs } from '~Utils/Settings';
import ModernButton from '~components/ModernButton';
import ModernCard from '~components/ModernCard/ModernCard';
import { ModernHeader } from '~components/ModernHeader';
import { useGetUserType } from '~hooks/event/useGetUserType';
import { useUpdateUser } from '~hooks/user/useUpdateUser';
import { useDeleteUser } from '~hooks/user/useDeleteUser';
import { spacing, borderRadius, typography, shadows } from '~constants/design';
// import { useChatStore } from '~providers/chats/zustand';
import { useIsFocused } from '@react-navigation/native';
import { useEffect, useState } from 'react';
// Conditionally import OneSignal for native platforms only
import * as oneSignalStub from '~services/web/oneSignalStub';
let OneSignal: any = null;
if (Platform.OS !== 'web') {
  OneSignal = require('react-native-onesignal').default;
} else {
  OneSignal = oneSignalStub.OneSignal;
}
import { useMapsContext } from '~providers/maps/zustand';
import { useUserStore } from '~providers/userStore/zustand';

import { NavigationProps } from '~types/navigation/navigation.type';
import getStyles from './styles';
import { SCREENS } from '~constants';
import { logScreenView } from '~Utils/firebaseAnalytics';
import { useTheme } from '~contexts/ThemeContext';
import Animated, { FadeInDown, SlideInUp } from 'react-native-reanimated';

const HelpCenter = () => {
  const { colors } = useTheme();
  const styles = getStyles(colors);
  const { navigate, dispatch } = useNavigation<NavigationProps>();
  const { t } = useTranslation();
  // const {clearState} = useChatStore();
  const user = useUserStore(state => state.user);
  const { data: userType } = useGetUserType(user?.uid);
  const { bottom } = useSafeAreaInsets();
  const { mutateAsync: updateUserMutation } = useUpdateUser();
  const { mutateAsync: deleteUserMutation } = useDeleteUser();
  const { resetMapsState } = useMapsContext();
  const { resetUser } = useUserStore();

  const [isLoading, setIsLoading] = useState(false);

  const isFocused = useIsFocused();

  useEffect(() => {
    setIsLoading(false);
  }, [isFocused]);

  useEffect(() => {
    logScreenView('Help Center', 'HelpCenter');
  }, []);

  const deleteAccount = async () => {
    try {
      setIsLoading(true);

      if (!user?.uid) {
        throw new Error('User ID not found');
      }

      // Call the delete account API
      await deleteUserMutation(user.uid);

      // Clear user coordinates if needed
      await updateUserMutation({
        coords_real: null,
      }).catch(() => {
        // Continue with deletion even if coordinate update fails
      });

      // Disable OneSignal push notifications
      try {
        OneSignal.disablePush(true);
        console.log('✅ OneSignal push notifications disabled');
      } catch (error) {
        console.error('❌ Failed to disable OneSignal push notifications:', error);
      }

      // Reset app state
      resetMapsState();
      resetUser();

      // Navigate to AUTH screen (splash/login)
      dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: SCREENS.AUTH }],
        })
      );

      console.log('✅ Account deleted successfully');
    } catch (error) {
      console.error('❌ Account deletion failed:', error);
      setIsLoading(false);
      
      Alert.alert(
        t('settings.deleteAccountErrorAlert.title') || 'Delete Account Failed',
        t('settings.deleteAccountErrorAlert.description') || 'Failed to delete account. Please try again or contact support.',
        [
          { onPress: contactUs, text: t('settings.deleteAccountErrorAlert.contact') || 'Contact Support', isPreferred: true },
          { style: 'cancel', text: t('settings.deleteAccountErrorAlert.cancel') || 'Cancel' },
        ]
      );
    }
  };

  const handleDeleteAccountAlert = async () => {
    Alert.alert(t('settings.deleteAccountAlert.title'), t('settings.deleteAccountAlert.description'), [
      { onPress: deleteAccount, text: t('settings.deleteAccountAlert.delete'), style: 'destructive' },
      { text: t('settings.deleteAccountAlert.cancel'), style: 'cancel', isPreferred: true },
    ]);
  };  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }}>
      <ModernHeader title={t('settings.help_center') || 'Help Center'} variant="default" />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: spacing.lg,
          paddingBottom: spacing['6xl'],
        }}
        showsVerticalScrollIndicator={false}>
        {/* Header Section */}
        <Animated.View entering={FadeInDown.delay(100).duration(400)}>
          <ModernCard variant="default" padding="xl" margin="md">
            <Text
              style={{
                fontSize: typography.fontSize['2xl'],
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textAlign: 'center',
                marginBottom: spacing.md,
              }}>
              {t('settings.contact_us')}
            </Text>
            <Text
              style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.normal,
                color: colors.textSecondary,
                textAlign: 'center',
                lineHeight: typography.fontSize.base * typography.lineHeight.relaxed,
              }}>
              {t('settings.contact_us_body')}
            </Text>
          </ModernCard>
        </Animated.View>

        {/* Support Actions */}
        <Animated.View entering={FadeInDown.delay(200).duration(400)}>
          <Text
            style={{
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              marginBottom: spacing.sm,
              marginTop: spacing.md,
            }}>
            {t('settings.support_actions') || 'Support Actions'}
          </Text>

          <ModernCard variant="default" padding="lg">
            <ModernButton
              title={t('settings.contact_us')}
              onPress={contactUs}
              variant="primary"
              size="lg"
              fullWidth
              hapticFeedback
              style={{ marginBottom: spacing.md }}
            />

            <ModernButton
              title={t('settings.delete_account')}
              onPress={handleDeleteAccountAlert}
              variant="outline"
              size="lg"
              fullWidth
              loading={isLoading}
              hapticFeedback
              style={{
                borderColor: colors.red,
              }}
              textStyle={{
                color: colors.red,
              }}
            />
          </ModernCard>
        </Animated.View>

        {/* Additional Help */}
        <Animated.View entering={FadeInDown.delay(300).duration(400)}>
          <Text
            style={{
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              marginBottom: spacing.sm,
              marginTop: spacing.lg,
            }}>
            {t('settings.additional_help') || 'Additional Help'}
          </Text>

          <ModernCard variant="default" padding="lg">
            <Text
              style={{
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.normal,
                color: colors.textSecondary,
                lineHeight: typography.fontSize.sm * typography.lineHeight.relaxed,
                marginBottom: spacing.md,
              }}>
              {t('settings.help_description') ||
                'Need more help? Check out our FAQ section or reach out to our support team directly.'}
            </Text>

            <ModernButton
              title={t('settings.view_faq') || 'View FAQ'}
              onPress={() => {
                /* Navigate to FAQ */
              }}
              variant="ghost"
              size="md"
              fullWidth
              hapticFeedback
            />
          </ModernCard>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default HelpCenter;
